// Bento лентата с показателите на „История на маси".
//
// Кутийките НЕ са равни: „Сметки" и „Оборот" носят по-едро число и по-широк
// трак, останалите три са компактни. В самия дизайн петте карти са равни
// (`repeat(auto-fit, minmax(150px, 1fr))`) — разликата в големините е
// съзнателна промяна, за да личи кое е водещото число на екрана.
//
// Всички стойности идват от `summary`, което BE-то смята за ЦЕЛИЯ период и
// САМО по датите, сервитьора и правото за достъп. Филтърът по статус, по метод
// на плащане и търсенето стесняват СПИСЪКА (и реда „Общо"), но не и тези числа
// — затова, когато такъв филтър е активен, лентата го казва с един ред отдолу,
// вместо да остави две различни бройки да си противоречат мълчаливо.

import { useTranslation } from "react-i18next";

import { fmtEuro } from "../../../../utils/currency";
import { formatMinutes, parseDecimal } from "../../../../components/dataTable";
import type { BillsHistorySummary } from "../../../../api/reportsBillsHistory";
import { FiscalSplit } from "../FiscalSplit";
import { ReportKpiCard } from "../ReportKpiCard";
import { deltaPercent } from "../reportDelta";

type Props = {
  summary: BillsHistorySummary | undefined;
  previousSummary: BillsHistorySummary | undefined;
  /** Изключен ли е сравнителният режим — тогава няма чипове с делта. */
  compare: boolean;
  /** false = BE е скрил закритите в брой без бон; „без бон" би бил непълен. */
  sessionAccess: boolean;
  /** Активен е филтър, който стеснява само списъка — виж бележката горе. */
  narrowed: boolean;
  /** Кликът върху втората група изолира точно тези сметки в списъка. */
  onIsolateMarked?: () => void;
  isolatedMarked?: boolean;
  isNarrow: boolean;
};

export const BillsHistoryKpis = ({
  summary,
  previousSummary,
  compare,
  sessionAccess,
  narrowed,
  onIsolateMarked,
  isolatedMarked = false,
  isNarrow,
}: Props) => {
  const { t } = useTranslation();

  const closedCount = summary?.closed_count ?? 0;
  const revenue = parseDecimal(summary?.total_revenue);
  const avgTicket = parseDecimal(summary?.avg_ticket);
  const avgDuration =
    summary?.avg_duration_minutes === null ||
    summary?.avg_duration_minutes === undefined
      ? null
      : parseDecimal(summary.avg_duration_minutes);
  const fiscalized = summary?.fiscalized_count ?? 0;
  const unfiscalized = summary?.unfiscalized_count ?? 0;

  const withDelta = (current: number, previous: number | undefined) =>
    compare && previousSummary
      ? deltaPercent(current, previous ?? 0)
      : undefined;

  return (
    <div className="flex flex-col gap-1.5">
    <div
      className={`grid gap-2.5 ${
        isNarrow
          ? "grid-cols-2"
          : "grid-cols-2 lg:grid-cols-[1.3fr_1.3fr_1fr_1fr]"
      }`}
    >
      <ReportKpiCard
        kicker={t("reportsPage.billsHistoryKpiBills")}
        value={String(closedCount)}
        // Справката показва САМО приключени сметки, затова числото е техният
        // брой, а под него стои разбивката БЕЗ ДУМИ — запълнено кръгче срещу
        // кръгче с контур, същият знак, който носи и редът в таблицата; до
        // всеки брой, след „|", стои и сумата му. Показва се само при сесия с
        // втори код И когато има какво да се раздели; иначе остава само
        // броят, без намек за втора група.
        sub={
          sessionAccess && unfiscalized > 0 ? (
            <FiscalSplit
              fiscalized={fiscalized}
              unfiscalized={unfiscalized}
              fiscalizedAmount={summary?.fiscalized_amount}
              unfiscalizedAmount={summary?.unfiscalized_amount}
              onToggle={onIsolateMarked}
              isolated={isolatedMarked}
            />
          ) : (
            t("reportsPage.billsHistoryKpiBillsUnit")
          )
        }
        delta={withDelta(closedCount, previousSummary?.closed_count)}
        hero
      />
      <ReportKpiCard
        kicker={t("reportsPage.billsHistoryKpiRevenue")}
        value={fmtEuro(revenue)}
        sub={t("reportsPage.billsHistoryKpiRevenueSub")}
        delta={withDelta(revenue, parseDecimal(previousSummary?.total_revenue))}
        hero
      />
      <ReportKpiCard
        kicker={t("reportsPage.billsHistoryKpiAvgTicket")}
        value={fmtEuro(avgTicket)}
        delta={withDelta(avgTicket, parseDecimal(previousSummary?.avg_ticket))}
      />
      <ReportKpiCard
        kicker={t("reportsPage.billsHistoryKpiAvgTime")}
        value={
          avgDuration === null
            ? "—"
            : formatMinutes(avgDuration, {
                hour: t("dataTable.hourShort"),
                minute: t("dataTable.minuteShort"),
              })
        }
        sub={t("reportsPage.billsHistoryKpiAvgTimeSub")}
      />
    </div>

      {narrowed ? (
        <p className="px-1 text-[11.5px] font-semibold leading-[1.4] text-muted">
          {t("reportsPage.billsHistoryKpiScopeNote")}
        </p>
      ) : null}
    </div>
  );
};
